import React, { useState, useEffect } from 'react'
import { Droppable, Draggable } from '@hello-pangea/dnd'
import { makeStyles } from '@material-ui/core'
import Typography from '../Typography'
import BoardViewCard from './BoardViewCard'
import type { Theme } from '@material-ui/core/styles'
import type { StyleClasses } from '../../styles'
import { useIsMobile } from '../../hooks'
import { formatCurrency, getPipelines } from '@paintscout/util/builder'
import VisibilityIcon from '@material-ui/icons/Visibility'
import VisibilityOffOutlinedIcon from '@material-ui/icons/VisibilityOffOutlined'
import classnames from 'classnames'
import PipelineProgressBar from '../PipelineProgressBar'
import { useClientOptions } from '../../providers/ClientOptionsProvider'
import type { Deal, DealSummaryResponse } from '@paintscout/api'
import Spinner from '../Spinner'
import Button from '../Button'

const useStyles = makeStyles<Theme>(
  (theme) => ({
    root: {
      position: 'relative',
      margin: theme.spacing(1),
      width: 300,
      flexShrink: 0,
      display: 'flex',
      flexDirection: 'column',
      '&:first-child': {
        marginLeft: 0
      },
      '&:last-child': {
        marginRight: 0
      }
    },
    rootCollapsed: {
      width: 'max-content'
    },
    labelWrapper: {
      cursor: 'pointer',
      padding: theme.spacing(0, 2),
      display: 'flex',
      alignItems: 'center',
      '& > svg': {
        margin: theme.spacing(0, 0, 0, 1),
        color: theme.paintscout.palette.brand.Neutral.P80,
        fontSize: 20,
        display: 'none'
      },
      '&:hover': {
        '& > svg': {
          display: 'block'
        }
      }
    },
    labelWrapperCollapsed: {
      maxHeight: '100vh',
      marginTop: 57,
      background: theme.paintscout.palette.brand.Neutral.P96,
      borderRadius: theme.borderRadius['md'],
      padding: theme.spacing(2, 0.5),
      transform: 'rotate(180deg)',
      justifyContent: 'end',
      writingMode: 'vertical-rl',
      height: '100%',
      '& > svg': {
        display: 'block',
        margin: theme.spacing(1, 0, 0, 0),
        transform: 'rotate(90deg)',
        color: theme.paintscout.palette.brand.Neutral.P80
      },
      '&:hover': {
        '& > svg': {
          color: theme.paintscout.palette.brand.Neutral.P70
        }
      },
      [theme.breakpoints.down('sm')]: {
        marginTop: 86
      }
    },
    label: {
      fontSize: '1.2rem',
      color: theme.paintscout.palette.primary,
      fontWeight: 500,
      display: 'flex',
      alignItems: 'center'
    },
    hiddenCount: {
      fontSize: '1rem',
      color: theme.paintscout.palette.brand.Neutral.P60,
      margin: theme.spacing(1, 0, 0, 0),
      lineHeight: 1
    },
    progressWrapper: {
      padding: theme.spacing(0, 2),
      margin: theme.spacing(4, 0, 1, 0),
      pointerEvents: 'none'
    },
    columnBody: {
      borderRadius: theme.borderRadius['md'],
      padding: theme.spacing(2, 2, 0, 2), // keeping last card marginBottom instead of using equal padding here so dragging last cards doesn't look weird
      background: theme.paintscout.palette.brand.Neutral.P96,
      height: '100%',
      maxHeight: '100vh',
      overflowY: 'auto',
      minHeight: 165
    },
    leadCalloutButton: {
      width: '100%',
      borderRadius: 0,
      backgroundColor: '#006bf5',
      color: 'var(--Brand-White, #FFF)',
      fontFamily: 'var(--font-family-Inter, Inter)',
      fontSize: 16,
      fontStyle: 'normal',
      fontWeight: 600,
      lineHeight: '18px',
      letterSpacing: 'var(--Style-Body1-letter-spacing-lg, 0)',
      textTransform: 'none',
      padding: theme.spacing(1.75),
      boxShadow: '0 12px 30px rgba(5, 119, 247, 0.55)',
      marginBottom: theme.spacing(1.5),
      transition: 'none',
      '&:focus, &:focus-visible': {
        outline: 'none',
        boxShadow: '0 12px 30px rgba(5, 119, 247, 0.55)'
      },
      '&:active': {
        transform: 'none',
        boxShadow: '0 12px 30px rgba(5, 119, 247, 0.55)'
      },
      '&:hover': {
        boxShadow: '0 12px 30px rgba(5, 119, 247, 0.55)'
      }
    },
    subHeading: {
      display: 'flex',
      justifyContent: 'space-between',
      padding: theme.spacing(1, 2)
    }
  }),
  { name: 'BoardViewColumn' }
)

export interface BoardViewColumnProps {
  classes?: StyleClasses<typeof useStyles>
  column: {
    label: string
    key: string
  }
  data: { deals: Deal[]; aggregates: DealSummaryResponse }
  keyField: string
  ContentsComponent: React.FC
  pipeline: string
  isDropDisabled?: boolean
  onLoadMore: () => void
  isLoading?: boolean
  isFetchingMore?: boolean
  hasMore?: boolean
  hasError?: boolean
  displayLeadCallout?: boolean
  newLeadCount?: number
  onMarkNewLeadsSeen?: () => void
}

function BoardViewColumn(props: BoardViewColumnProps) {
  const classes = useStyles(props)
  const {
    column,
    data,
    keyField,
    ContentsComponent,
    isDropDisabled,
    pipeline,
    onLoadMore,
    isFetchingMore,
    isLoading,
    hasError,
    hasMore,
    displayLeadCallout,
    newLeadCount,
    onMarkNewLeadsSeen
  } = props
  const isMobile = useIsMobile()
  const { options } = useClientOptions()
  const pipelines = getPipelines({ options })
  const fullPipeline = pipelines.find((p) => p.key === pipeline)

  const stageKey = column.key
  const collapsedStagesStorageKey = `collapsedStages_${pipeline}`

  const totalValue = data?.aggregates?.totalDealValue || 0
  const totalNewLeadCount = newLeadCount ?? 0
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const stored = window.localStorage.getItem(collapsedStagesStorageKey)
    const stages: string[] = stored ? JSON.parse(stored) : []
    return stages.includes(stageKey)
  })
  const [calloutVisible, setCalloutVisible] = useState(false)

  useEffect(() => {
    if (!displayLeadCallout || totalNewLeadCount === 0) {
      setCalloutVisible(false)
      return
    }

    setCalloutVisible(true)
    const timer = window.setTimeout(() => {
      setCalloutVisible(false)
      onMarkNewLeadsSeen?.()
    }, 3000)
    return () => {
      window.clearTimeout(timer)
    }
  }, [displayLeadCallout, totalNewLeadCount, onMarkNewLeadsSeen])

  const handleCollapse = () => {
    const current = window.localStorage.getItem(collapsedStagesStorageKey)
    const stages: string[] = current ? JSON.parse(current) : []
    if (stages.includes(stageKey)) {
      stages.splice(stages.indexOf(stageKey), 1)
    } else {
      stages.push(stageKey)
    }
    window.localStorage.setItem(collapsedStagesStorageKey, JSON.stringify(stages))
    setIsCollapsed(!isCollapsed)
  }

  const VisibilityToggleIcon = isCollapsed ? VisibilityOffOutlinedIcon : VisibilityIcon
  const dealCount = data?.aggregates?.totalDeals || 0
  const leadPulseClass = calloutVisible ? 'animate__animated animate__pulse' : 'animate__animated'

  return (
    <Droppable droppableId={column.key} isDropDisabled={isDropDisabled || isCollapsed}>
      {(droppableProvided, _snapshot) => {
        return (
          <div
            className={classnames(classes.root, { [classes.rootCollapsed]: isCollapsed })}
            ref={droppableProvided.innerRef}
            {...droppableProvided.droppableProps}
          >
            <div
              className={classnames(classes.labelWrapper, { [classes.labelWrapperCollapsed]: isCollapsed })}
              onClick={handleCollapse}
            >
              <Typography variant="h3" className={classes.label}>
                {column.label}
                {isCollapsed ? <span className={classes.hiddenCount}>{`${dealCount}`}</span> : ''}
              </Typography>
              <VisibilityToggleIcon />
            </div>

            {!isCollapsed && (
              <>
                <div className={classes.subHeading}>
                  <div>{dealCount}</div>
                  <div>{formatCurrency({ value: totalValue, options })}</div>
                </div>
                <div className={classes.progressWrapper}>
                  {isMobile && <PipelineProgressBar pipeline={fullPipeline} fixedStageId={stageKey} />}
                </div>
                <div className={classes.columnBody}>
                  {displayLeadCallout && calloutVisible && totalNewLeadCount > 0 && (
                    <Button
                      className={`${leadPulseClass} ${classes.leadCalloutButton}`}
                      disableElevation
                      disableRipple
                      disableFocusRipple
                      aria-label={`${totalNewLeadCount} unseen lead${totalNewLeadCount === 1 ? '' : 's'} since your last visit`}
                      style={{ animationIterationCount: 3 }}
                    >
                      {`${totalNewLeadCount} New Lead${totalNewLeadCount === 1 ? '' : 's'}`}
                    </Button>
                  )}
                  {data?.deals?.length ? (
                    data.deals.map((cardData, index) => {
                      const key = cardData?.[keyField]
                      if (!key) {
                        console.log({
                          key,
                          'cardData?.[keyField]': cardData?.[keyField],
                          index,
                          cardData,
                          keyField
                        })
                      }
                      return (
                        <BoardViewCard
                          ContentsComponent={ContentsComponent}
                          data={cardData}
                          index={index}
                          key={key}
                          draggableId={key}
                        />
                      )
                    })
                  ) : (
                    <>
                      {/* This is a workaround to give dnd context as to where to place a card in an empty col. */}
                      <Draggable draggableId={`empty-${column.key}`} index={0} isDragDisabled={true}>
                        {(provided, _snapshot) => {
                          return (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                            ></div>
                          )
                        }}
                      </Draggable>
                      {isLoading ? (
                        <Spinner style={{ marginTop: 16 }} />
                      ) : (
                        <Typography variant="subtitle1" style={{ textAlign: 'center', marginTop: 56 }}>
                          {hasError ? 'Error loading deals.' : 'No deals found in this stage.'}
                        </Typography>
                      )}
                    </>
                  )}
                  {droppableProvided.placeholder}
                  {hasMore && !isLoading && (
                    <div style={{ textAlign: 'center', padding: 8 }}>
                      <Button variant="text" onClick={onLoadMore} disabled={isFetchingMore}>
                        {isFetchingMore ? 'Loading...' : 'Load more'}
                      </Button>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>
        )
      }}
    </Droppable>
  )
}

export default BoardViewColumn
